import { TourAnchor } from "training/anchors";
import {
  directPayEnabled,
  hasEstimates,
  hasMultipleAccounts,
  not,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

/**
 * CLE-3015 Module 1: Logging in and the Estimates page. Text is the curriculum's (curriculum-v2, 2026-10-21),
 * verbatim. Two curriculum rows point at two elements each (1.7 the date and status filters, 1.9 View and
 * the "..." menu); each is split into two steps at a sentence boundary, so the words are unchanged and
 * each element gets its own highlight. The row steps (1.8, 1.9) need a first row; on a hospital with no
 * estimates one card (`m1.08n-row-controls-card`, not curriculum text) replaces them.
 */
export const MODULE_1: ITourStep[] = [
  {
    id: "m1.00-login-card",
    module: 1,
    mode: "card",
    route: "estimates",
    text: [
      'You sign in with your email and the password you set from the welcome email. There is no "forgot password" inside the app. Use "Reset password" on the sign-in page.',
      'After your password you are asked for a 6-digit security code. It is sent to your email unless Clear Health has set your account to text it to your phone. Check spam the first time and mark it "not spam", because the code and the password-reset email both come from the same sender.',
      'The code is yours alone. Never share it. If it says expired, click "Resend Code".',
      'Accounts that go unused for 90 days are disabled automatically. You get a warning email first. If you ever see "Your access has been disabled", email support@clearhealthinc.com.',
    ].join("\n"),
  },
  {
    id: "m1.01-estimates-tab",
    module: 1,
    anchor: TourAnchor.EstimatesTab,
    label: "Estimates",
    mode: "look",
    route: "estimates",
    text: "This is your home page. Everything you do day to day starts from this tab. There is no side menu; the tabs across the top, plus your name menu at the right, are the whole app.",
  },
  {
    id: "m1.02-facility-switcher",
    module: 1,
    anchor: TourAnchor.FacilitySwitcher,
    mode: "look",
    route: "estimates",
    when: hasMultipleAccounts,
    text: "If you work at more than one facility, pick it here. The red number is how many estimates there need attention. The toggle sets which one opens when you log in.",
  },
  {
    id: "m1.03-monthly-collections",
    module: 1,
    anchor: TourAnchor.MonthlyCollections,
    label: "Monthly collections",
    mode: "look",
    route: "estimates",
    text: "Expected is what this month's patients, by date of service, owe through Clear Health. Collected is what they have paid so far, minus refunds. The gauge is the share collected.",
  },
  {
    id: "m1.04-estimates-table",
    module: 1,
    anchor: TourAnchor.EstimatesTable,
    mode: "look",
    route: "estimates",
    text: "Every estimate your team sends lands here, newest first. One row per patient visit.",
  },
  {
    id: "m1.05-status-column",
    module: 1,
    anchor: TourAnchor.StatusColumn,
    label: "STATUS",
    mode: "look",
    route: "estimates",
    // One status per line; the popover renders "Label: explanation" lines as a tight list.
    text: [
      "Sent: texted, not paid.",
      "Partial: part paid, or on a plan.",
      "Paid: paid in full.",
      "Action Needed: a draft, or insurance came back inactive.",
      "Expired: the visit passed unpaid.",
      "You may also see No Balance Due, Paid via Cash, Refunded and Canceled.",
    ].join("\n"),
  },
  {
    id: "m1.06-search-box",
    module: 1,
    anchor: TourAnchor.SearchBox,
    label: "Search by patient name, payer, or account number",
    mode: "look",
    route: "estimates",
    text: "Find a patient fast. It also matches the account number from your system and the insurance member ID.",
  },
  {
    id: "m1.07-date-filter",
    module: 1,
    anchor: TourAnchor.DateFilter,
    label: "Date of service:",
    mode: "look",
    route: "estimates",
    text: "To see who is coming in today and has not paid, set the date to Today and the status to Sent. That is the list you work from each morning.",
  },
  {
    id: "m1.07b-status-filter",
    module: 1,
    anchor: TourAnchor.StatusFilter,
    label: "Filter by:",
    mode: "look",
    route: "estimates",
    text: "Today plus Paid gives you the end-of-day list of who paid.",
  },
  {
    id: "m1.08-collect-payment",
    module: 1,
    anchor: TourAnchor.CollectPaymentFirstRow,
    label: "Collect Payment",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: "A patient is at the desk and wants to pay. This opens their payment page for you. It only appears on Sent estimates; for one that is partly paid, open it with View.",
  },
  {
    id: "m1.09-view",
    module: 1,
    anchor: TourAnchor.ViewFirstRow,
    label: "View",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: "View opens the full estimate.",
  },
  {
    id: "m1.09b-row-menu",
    module: 1,
    anchor: TourAnchor.RowMenuFirstRow,
    label: "Estimate actions",
    mode: "look",
    route: "estimates",
    when: hasEstimates,
    text: "The menu has View, Resend, Duplicate, Copy link and Cancel. We come back to these in Module 3.",
  },
  {
    // Not curriculum text — Module 1 runs before the trainee has created anything, and a hospital with no
    // estimates has no first row: one card stands in for 1.8 and 1.9.
    id: "m1.08n-row-controls-card",
    module: 1,
    mode: "card",
    route: "estimates",
    when: not(hasEstimates),
    text: 'Once estimates exist, each row has three controls. Collect Payment, on Sent estimates only, opens the patient\'s payment page for you when they are at the desk. View opens the full estimate. The "…" menu has View, Resend, Duplicate, Copy link and Cancel. You will see them on your own estimate after Module 2.',
  },
  {
    id: "m1.10-direct-pay",
    module: 1,
    anchor: TourAnchor.DirectPay,
    label: "Direct Pay",
    mode: "look",
    route: "estimates",
    when: directPayEnabled,
    text: "For a payment that is not tied to an estimate, such as an old balance. Module 4 covers it.",
  },
  {
    id: "m1.11-new-estimate",
    module: 1,
    anchor: TourAnchor.NewEstimate,
    label: "New Estimate",
    mode: "look",
    route: "estimates",
    text: "Next, you create one.",
  },
  {
    id: "m1.12-daily-email-card",
    module: 1,
    mode: "card",
    route: "estimates",
    text: [
      "Each weekday at 4:00 AM Central, Clear Health emails a list of today's patients who still owe. It goes to the addresses your hospital gave us. Ask support if you should be on it. Weekend appointments are not emailed.",
      "Stay logged in for your shift. Each sign-in needs a new security code, so one login a day saves you doing it twice.",
    ].join("\n"),
  },
];
